import dayjs from 'dayjs';
import { Broadcast } from 'models/broadcast.model';
import { DraftBroadcastValidationField } from './broadcast.type';
import { dateWithTimeToString } from '../../hooks/helpers/date-time-helper';
import { DATE_FORMAT } from '../../common';

export const TITLE_CHARACTER_LIMIT = 100;
export const MESSAGE_CHARACTER_LIMIT = 900;

export const validateTitle = (
  title: string,
  fullValidation = false,
): string => {
  if (fullValidation && !title.length) {
    return 'Title is required';
  }

  if (title.length > TITLE_CHARACTER_LIMIT) {
    return `Title must be less than ${TITLE_CHARACTER_LIMIT} characters long. You are ${
      title.length - TITLE_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateMessage = (
  message: string,
  fullValidation = false,
): string => {
  if (fullValidation && !message.length) {
    return 'Message is required';
  }

  if (message.length > MESSAGE_CHARACTER_LIMIT) {
    return `Message must be less than ${MESSAGE_CHARACTER_LIMIT} characters long. You are ${
      message.length - MESSAGE_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateTagIds = (
  tagIds: string[],
  fullValidation = false,
  isBroadcastTagsEnabled = true,
): string => {
  if (!isBroadcastTagsEnabled) {
    return '';
  }

  if (fullValidation && !tagIds.length) {
    return 'At least one tag is required';
  }

  return '';
};

export const isSelectedDateAfterCurrentDate = (date: string) => {
  const now = dateWithTimeToString();
  const timeInNearFuture = dayjs(date)
    .subtract(1, 'minute')
    .format(DATE_FORMAT);

  return dayjs(timeInNearFuture).isAfter(dayjs(now));
};

export const validateScheduleDate = (
  scheduleDate: string,
  fullValidation = false,
  validationOnApprove = false,
): string => {
  if (fullValidation && !scheduleDate) {
    return 'Date is required';
  }

  if (validationOnApprove && !isSelectedDateAfterCurrentDate(scheduleDate)) {
    return 'Date / time should be in the future. Please, reject and update date.';
  }

  return '';
};

export const validateScheduleTime = (
  scheduleTime: string,
  fullValidation = false,
  validationOnApprove = false,
): string => {
  if (fullValidation && !scheduleTime) {
    return 'Time is required';
  }

  if (validationOnApprove && !isSelectedDateAfterCurrentDate(scheduleTime)) {
    return 'Date / time should be in the future. Please, reject and update date.';
  }

  return '';
};

export const validateBroadcast = ({
  broadcast,
  fullValidation = true,
  validationOnApprove = false,
  isBroadcastTagsEnabled = true,
}: {
  broadcast: Broadcast;
  fullValidation: boolean;
  validationOnApprove: boolean;
  isBroadcastTagsEnabled?: boolean;
}): DraftBroadcastValidationField => {
  const { title, message, tagIds, schedule } = broadcast;

  return {
    title: validateTitle(title, fullValidation),
    message: validateMessage(message, fullValidation),
    tagIds: validateTagIds(tagIds, fullValidation, isBroadcastTagsEnabled),
    schedule: validateScheduleDate(
      schedule?.toString() || '',
      fullValidation,
      validationOnApprove,
    ),
    scheduleTime: validateScheduleTime(
      schedule?.toString() || '',
      fullValidation,
      validationOnApprove,
    ),
  };
};
